import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { create as createApi, auth } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { flagClass, actionClass, fmtAmt, fmtDate, priceLabel, vendorLabel } from "@/lib/flags";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import Combobox from "@/components/Combobox";
import { ArrowLeft, Plus, Trash2, Save, Send, Loader2, Sparkles, AlertTriangle, FileText, Search, RefreshCw } from "lucide-react";
import { ItemMasterSyncButton } from "@/components/ItemMasterSyncButton";

const MAX_EXTRA_PER = 10;
const isKgm = (u) => (u || "").trim().toUpperCase() === "KGM";
const clampExtra = (unit, v) => (isKgm(unit) ? Math.min(MAX_EXTRA_PER, Math.max(0, Number(v) || 0)) : 0);


export default function CreatePR() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { vocNo: editVocNo } = useParams();   // present when route is /pr/edit/:vocNo
  const isEditMode = !!editVocNo;
  const me = auth.user();
  const [supplier, setSupplier] = useState(null);
  const [suppQuery, setSuppQuery] = useState("");
  const [suppOptions, setSuppOptions] = useState([]);
  const [purcType, setPurcType] = useState("BOP");
  const [remark, setRemark] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [authBy, setAuthBy] = useState("");
  const [appBy, setAppBy] = useState("");
  // Extended header fields (mapped to PoRequest columns)
  const [compcode, setCompcode] = useState("P1");      // Plant
  const [deptId, setDeptId] = useState("");
  const [matGroup, setMatGroup] = useState("");
  const [gst, setGst] = useState("18");
  const [endCust, setEndCust] = useState("");
  const [endCustName, setEndCustName] = useState("");
  const [endCustQ, setEndCustQ] = useState("");
  const [endCustOpts, setEndCustOpts] = useState([]);
  const [currency, setCurrency] = useState("INR");
  const [exchRate, setExchRate] = useState(1);
  const [inHouse, setInHouse] = useState("No");
  const [requestorName, setRequestorName] = useState("");
  const [requestorEmpNo, setRequestorEmpNo] = useState("");
  const [requestorQ, setRequestorQ] = useState("");
  const [requestorOpts, setRequestorOpts] = useState([]);
  const [masters, setMasters] = useState({ plants: [], depts: [], material_groups: [], material_types: [], gst_rates: [], currencies: [], auth_mediums: [], purc_types: [] });
  const [policyRules, setPolicyRules] = useState([]);  // [{id, name, priority, active, purc_types[], min_amount, max_amount, l1_emp_no, l1_name, l2_emp_no, l2_name}]
  // When we arrived from the MRP Proposals inbox with ?mrp_proposal_id=... we
  // hold onto the id so submit() can call /mrp-proposals/{id}/mark-converted
  // after the PR is saved. That marks the source proposal as sourced so it
  // disappears from the buyer inbox.
  const [mrpProposalId, setMrpProposalId] = useState(null);

  function emptyItem() {
    // _key is a stable client-side id used as React key — avoids reconciliation bugs when items reorder/remove
    return { _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
             icode: "", iname: "", qty: 1, rate: 0, unit: "", remark: "", extra_per: 0, mat_code: "", _opts: [], _q: "" };
  }

  const loadDrafts = useCallback(async () => {
    try { const { data } = await createApi.listDrafts(); setDrafts(data || []); return data || []; } catch (e) { console.warn("listDrafts failed", e); return []; }
  }, []);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  // Edit mode: fetch the PR via /prs/{voc_no}/edit-payload (shaped exactly
  // like this form expects) and pre-fill all state. Skips draft logic
  // entirely — saves go directly via PUT /prs/{voc_no}.
  const editLoadedRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || editLoadedRef.current) return;
    editLoadedRef.current = true;
    createApi.editPayload(editVocNo)
      .then(({ data }) => {
        setPurcType(data.purc_type || "BOP");
        setRemark(data.remark || "");
        setSupplier({ pcode: data.pcode, party: data.party });
        setCompcode(data.compcode || "P1");
        setDeptId(data.dept_id || "");
        setMatGroup(data.material_group || "");
        setGst(data.gst || "18");
        setEndCust(data.end_cust || "");
        setCurrency(data.currency || "INR");
        setExchRate(data.exch_rate || 1);
        setInHouse(data.in_house || "No");
        setRequestorEmpNo(data.requestor_emp_no || "");
        setRequestorName(data.requestor_name || "");
        setAuthBy(data.auth_by || "");
        setAppBy(data.app_by || "");
        if (Array.isArray(data.items) && data.items.length) {
          setItems(data.items.map((it) => ({
            _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `it-${Math.random().toString(36).slice(2, 9)}`,
            icode: it.icode || "", iname: it.iname || "",
            qty: Number(it.qty) || 1, rate: Number(it.rate) || 0,
            unit: it.unit || "", remark: it.remark || "",
            extra_per: clampExtra(it.unit, it.extra_per),
            mat_code: it.mat_code != null ? String(it.mat_code) : "",
            _opts: [], _q: it.iname || "",
          })));
        }
        toast.success(`Loaded PR #${editVocNo} for editing`);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not load PR for editing");
        setTimeout(() => nav("/my-prs"), 1200);
      });
  }, [isEditMode, editVocNo, nav]);

  // Auto-load a draft if /create?draft=<id> is passed (used by Clone-to-Draft from MyPRs)
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    const did = searchParams.get("draft");
    if (!did || autoLoadedRef.current) return;
    autoLoadedRef.current = true;
    createApi.listDrafts()
      .then(({ data }) => {
        setDrafts(data || []);
        const d = (data || []).find((x) => x.id === did);
        if (d) {
          loadDraft(d);
          toast.success(`Loaded cloned draft (from PR #${d.cloned_from_voc_no || "—"})`);
        } else {
          toast.error("Draft not found — it may have expired");
        }
      })
      .catch(() => toast.error("Could not load the cloned draft"));
  }, [searchParams]);

  // Pre-fill from MRP Proposals inbox (?icode=X&qty=N&uom=U&site=P1&delivery=Y-M-D&mrp_proposal_id=...&remark=...)
  // Fires once on mount. Only fills fields that aren't already set (so a
  // reload with the same query params doesn't clobber user edits). The
  // item's rate is left at 0 — buyer picks a vendor first, then the rate
  // auto-fills from Last PO / list-price on that pick (existing flow).
  const mrpPrefilledRef = useRef(false);
  useEffect(() => {
    if (mrpPrefilledRef.current || isEditMode) return;
    const proposalId = searchParams.get("mrp_proposal_id");
    const icode = searchParams.get("icode");
    if (!proposalId || !icode) return;
    mrpPrefilledRef.current = true;
    setMrpProposalId(proposalId);
    const qty = Number(searchParams.get("qty") || 0);
    const uom = searchParams.get("uom") || "";
    const site = searchParams.get("site");
    const preRemark = searchParams.get("remark") || "";
    if (site && ["P1","P2","P3","P4","P5","P6","P7","P8","P9"].includes(site)) {
      setCompcode(site);
    }
    if (preRemark) setRemark(preRemark);
    // Load master item so iname/unit/mat_code auto-populate — reuses the
    // same lookup the Combobox uses when a user types a code.
    createApi.searchItems(icode).then(({ data }) => {
      const hit = (data || []).find(o => (o.icode || "").toUpperCase() === icode.toUpperCase())
                || (data || [])[0];
      setItems([{
        _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `mrp-${Date.now()}`,
        icode: hit?.icode || icode,
        iname: hit?.iname || "",
        qty: qty || 1,
        rate: 0,
        unit: hit?.unit || uom || "",
        remark: `MRP proposal #${searchParams.get("mrp_proposal_id") || ""}`,
        extra_per: 0,
        mat_code: hit?.mat_code || "",
        _opts: [],
        _q: hit?.iname || icode,
      }]);
      toast.success(`Pre-filled from MRP proposal — pick a supplier to set the rate`);
    }).catch(() => {
      // Fallback: even without a master hit, still put the code in.
      setItems([{
        _key: `mrp-${Date.now()}`, icode, iname: "", qty: qty || 1,
        rate: 0, unit: uom, remark: `MRP proposal #${proposalId}`,
        extra_per: 0, mat_code: "", _opts: [], _q: icode,
      }]);
    });
  }, [searchParams, isEditMode]);

  // load master data (Plant / Dept / Material Group / etc.) once
  useEffect(() => {
    createApi.masterData()
      .then(({ data }) => { setMasters(data); if (data.purc_types?.length) setPurcType((p) => p || data.purc_types[0]); })
      .catch((e) => console.warn("masterData failed", e));
  }, []);

  // load active approval-policy rules. Re-fetch when the Requestor changes so
  // per-requestor L2 overrides (configured server-side) flow into the L2
  // selector — e.g. if Pawan Kumar (100018) is the requestor, Rajiv Maheshwari
  // becomes a selectable L2 even though the policy rule doesn't include him.
  useEffect(() => {
    createApi.approvalPolicy(requestorEmpNo || undefined)
      .then(({ data }) => setPolicyRules(data.rules || []))
      .catch((e) => console.warn("approvalPolicy failed", e));
  }, [requestorEmpNo]);

  // search suppliers
  useEffect(() => {
    const t = setTimeout(async () => {
      if (suppQuery.length < 2) return;
      try { const { data } = await createApi.searchSuppliers(suppQuery); setSuppOptions(data); }
      catch (e) { console.warn("searchSuppliers failed", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [suppQuery]);

  // search end-user (uses same party master)
  useEffect(() => {
    const t = setTimeout(async () => {
      if (endCustQ.length < 2) return;
      try { const { data } = await createApi.searchSuppliers(endCustQ); setEndCustOpts(data); }
      catch (e) { console.warn("searchSuppliers (endCust) failed", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [endCustQ]);

  // Load the FULL active-employee list once on mount (served from cache, ~15KB).
  // After this the Requestor dropdown filters client-side and feels instant.
  const [allEmployees, setAllEmployees] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await createApi.listEmployees();
        if (!cancelled) setAllEmployees(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn("listEmployees failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Requestor dropdown — filter the in-memory list as the user types.
  useEffect(() => {
    const q = (requestorQ || "").trim().toLowerCase();
    if (q.length < 1) { setRequestorOpts([]); return; }
    const matches = [];
    for (const e of allEmployees) {
      const name = (e.full_name || "").toLowerCase();
      const user = (e.username || "").toLowerCase();
      const emp = (e.emp_no || "").toLowerCase();
      if (name.includes(q) || user.includes(q) || emp.includes(q)) {
        matches.push(e);
        if (matches.length >= 20) break;
      }
    }
    setRequestorOpts(matches);
  }, [requestorQ, allEmployees]);

  // Prefill Requestor with logged-in user's emp_no + full name (creator can override)
  useEffect(() => {
    if (!requestorEmpNo && me?.emp_no) {
      setRequestorEmpNo(me.emp_no);
      setRequestorName(me.full_name || "");
    }
  }, [me?.emp_no, me?.full_name]);

  // recompute preview when supplier/items change
  useEffect(() => {
    if (!supplier?.pcode || items.length === 0) { setPreview(null); return; }
    const valid = items.filter((i) => i.icode && i.rate > 0 && i.qty > 0);
    if (valid.length === 0) { setPreview(null); return; }
    const t = setTimeout(async () => {
      setPreviewBusy(true);
      try {
        const { data } = await createApi.preview({
          pcode: supplier.pcode, purc_type: purcType,
          items: valid.map((i) => ({ icode: i.icode, iname: i.iname, qty: i.qty, rate: i.rate, unit: i.unit })),
        });
        setPreview(data);
      } catch (e) { console.error("preview failed", e); setPreview(null); } finally { setPreviewBusy(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [supplier, purcType, items]);

  const addItem = () => setItems((x) => [...x, emptyItem()]);
  const removeItem = (idx) => setItems((x) => x.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) => setItems((x) => x.map((it, i) => {
    if (i !== idx) return it;
    const next = { ...it, ...patch };
    if (!isKgm(next.unit)) next.extra_per = 0;
    return next;
  }));

  // ------------------------------------------------------------------
  // BOP × SAP-match backfill
  // ------------------------------------------------------------------
  // Items picked from the /items/search dropdown carry `sap_match` in
  // the response, so their pill renders instantly. Items that arrive
  // via MRP proposal prefill, edit-mode payload, or manual paste have
  // no such field — for BOP PRs we batch-resolve them via
  // POST /items/sap-match so the pill + rollup banner stay accurate.
  // Non-BOP PRs skip the call entirely.
  useEffect(() => {
    if (purcType !== "BOP") return;
    const need = Array.from(new Set(
      items.filter((it) => it.icode && it.sap_match === undefined)
           .map((it) => it.icode)
    ));
    if (need.length === 0) return;
    let cancelled = false;
    createApi.sapMatchIcodes(need).then(({ data }) => {
      if (cancelled) return;
      const matched = data?.matched || {};
      setItems((arr) => arr.map((it) => (
        it.icode && it.sap_match === undefined && matched[it.icode] !== undefined
          ? { ...it, sap_match: !!matched[it.icode] }
          : it
      )));
    }).catch((e) => console.warn("sapMatchIcodes failed", e));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purcType, items.map((it) => it.icode).join("|")]);
  const bopSapMissing = purcType === "BOP"
    ? items.filter((it) => it.icode && it.sap_match === false).map((it) => it.icode)
    : [];

  // ------------------------------------------------------------------
  // BOP × BOM-membership backfill (hard-block, pallet exemption)
  // ------------------------------------------------------------------
  // Rule (Sep-25-2026): a BOP PR can only be raised for a material
  // that's consumed in a Released BOM — with one carve-out: pallets
  // (item codes starting with `PALL-`) are packaging consumables and
  // are never part of a BOM. The backend `_bom_membership` helper
  // already applies this exemption, so the frontend just mirrors the
  // boolean it returns.
  useEffect(() => {
    if (purcType !== "BOP") return;
    const need = Array.from(new Set(
      items.filter((it) => it.icode && it.in_bom === undefined)
           .map((it) => it.icode)
    ));
    if (need.length === 0) return;
    let cancelled = false;
    createApi.bomCheckIcodes(need).then(({ data }) => {
      if (cancelled) return;
      setItems((arr) => arr.map((it) => (
        it.icode && it.in_bom === undefined && data[it.icode] !== undefined
          ? { ...it, in_bom: !!data[it.icode] }
          : it
      )));
    }).catch((e) => console.warn("bomCheckIcodes failed", e));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purcType, items.map((it) => it.icode).join("|")]);
  const bopBomMissing = purcType === "BOP"
    ? items.filter((it) => it.icode && it.in_bom === false).map((it) => it.icode)
    : [];

  // ------------------------------------------------------------------
  // BOP × Purchasing-view active-status backfill (hard-block)
  // ------------------------------------------------------------------
  // Rule (Sep-26-2026): every BOP line's material must have an
  // Active Purchasing view in SAP (`LifeCycleStatusCode == "2"`).
  // Backend `_purchasing_active` already applies the pallet exemption
  // (`PALL-*` → treated as active), so the frontend just mirrors the
  // returned booleans + labels.
  useEffect(() => {
    if (purcType !== "BOP") return;
    const need = Array.from(new Set(
      items.filter((it) => it.icode && it.purch_active === undefined)
           .map((it) => it.icode)
    ));
    if (need.length === 0) return;
    let cancelled = false;
    createApi.purchasingCheckIcodes(need).then(({ data }) => {
      if (cancelled) return;
      setItems((arr) => arr.map((it) => (
        it.icode && it.purch_active === undefined && data[it.icode] !== undefined
          ? { ...it,
              purch_active: !!data[it.icode].active,
              purch_status_label: data[it.icode].status_label || "" }
          : it
      )));
    }).catch((e) => console.warn("purchasingCheckIcodes failed", e));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purcType, items.map((it) => it.icode).join("|")]);
  const bopPurchInactive = purcType === "BOP"
    ? items.filter((it) => it.icode && it.purch_active === false).map((it) => it.icode)
    : [];

  // ------------------------------------------------------------------
  // BOP price waterfall — SAP list price first, MSSQL last_rate fallback
  // ------------------------------------------------------------------
  // Runs whenever supplier / items change on a BOP PR. Behaviour:
  //   • On supplier change → overwrite EVERY item's rate with the fresh
  //     quote (a new contract can move rates).
  //   • Otherwise → only overwrite rates that are 0 or that WE previously
  //     auto-filled (`pricedBy` set) — never stomp a rate the buyer typed.
  //   • Non-BOP purc_types skip the call entirely.
  const lastQuotedPcodeRef = useRef(null);
  const quoteTimerRef = useRef(null);
  useEffect(() => {
    if (purcType !== "BOP") return;
    const pcode = supplier?.pcode || "";
    if (!pcode) return;
    const withIcode = items.filter((it) => it.icode);
    if (withIcode.length === 0) return;
    const supplierChanged = lastQuotedPcodeRef.current !== null
                            && lastQuotedPcodeRef.current !== pcode;
    // Debounce so a burst of qty keystrokes doesn't spam the endpoint.
    if (quoteTimerRef.current) clearTimeout(quoteTimerRef.current);
    quoteTimerRef.current = setTimeout(async () => {
      try {
        const payload = withIcode.map((it) => ({
          icode: it.icode, qty: Number(it.qty) || 0, unit: it.unit || "",
        }));
        const { data } = await createApi.quoteBopItems(pcode, "BOP", payload);
        const byIcode = {};
        for (const q of (data?.quotes || [])) byIcode[q.icode] = q;
        setItems((arr) => arr.map((it) => {
          if (!it.icode) return it;
          const q = byIcode[it.icode];
          if (!q) return it;
          // STRICT BOP policy: only auto-fill rate from SAP list price.
          // If no SAP price (source==="none") we reset rate to 0 and
          // surface the blocking `no_sap_price_msg`. Never fall back to
          // the picker's MSSQL rate for BOP.
          const nextRate = (q.source === "sap_list_price" && !q.uom_mismatch)
                             ? q.rate
                             : 0;
          return {
            ...it,
            rate: nextRate,
            pricedBy: q.source === "sap_list_price" ? q.source : undefined,
            rateSource: q.source,
            sapValidFrom: q.sap_valid_from,
            sapValidTo: q.sap_valid_to,
            lastVdate: q.last_vdate,
            uomMismatch: q.uom_mismatch,
            uomMismatchMsg: q.uom_mismatch_msg,
            noSapPrice: q.no_sap_price,
            noSapPriceMsg: q.no_sap_price_msg,
            notSmartPr: q.not_smart_pr,
            lpBlockLabel: q.lp_block_label,
            rateCurrency: q.currency_code,
            rateUnitCode: q.unit_code,
            sapAmount: q.sap_amount,
            sapBaseQty: q.sap_base_quantity,
          };
        }));
        lastQuotedPcodeRef.current = pcode;
      } catch (e) {
        console.warn("quoteBopItems failed", e);
      }
    }, 350);
    return () => { if (quoteTimerRef.current) clearTimeout(quoteTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purcType, supplier?.pcode,
      items.map((it) => `${it.icode}|${it.qty}|${it.unit}`).join("~")]);

  // Live "Fetch Latest LP" — hits SAP OData with the tightest filter for
  // this (supplier, item), upserts the returned rows into the local
  // mirror, and applies the fresh quote to just this line. Use after
  // creating/releasing a price in SAP BYD.
  const fetchLatestLp = async (idx) => {
    const it = items[idx];
    if (!it || !it.icode || !supplier?.pcode) return;
    updateItem(idx, { fetchingLp: true });
    try {
      const { data: q } = await createApi.fetchLpLive(
        supplier.pcode, it.icode, Number(it.qty) || 0, it.unit || "",
      );
      const nextRate = (q.source === "sap_list_price" && !q.uom_mismatch)
                         ? q.rate : 0;
      updateItem(idx, {
        rate: nextRate,
        pricedBy: q.source === "sap_list_price" ? q.source : undefined,
        rateSource: q.source,
        sapValidFrom: q.sap_valid_from,
        sapValidTo: q.sap_valid_to,
        uomMismatch: q.uom_mismatch,
        uomMismatchMsg: q.uom_mismatch_msg,
        noSapPrice: q.no_sap_price,
        noSapPriceMsg: q.no_sap_price_msg,
        notSmartPr: q.not_smart_pr,
        lpBlockLabel: q.lp_block_label,
        rateCurrency: q.currency_code,
        rateUnitCode: q.unit_code,
        sapAmount: q.sap_amount,
        sapBaseQty: q.sap_base_quantity,
        fetchingLp: false,
      });
      if (q.source === "sap_list_price") {
        toast.success(`SAP list price fetched: ${q.rate} ${q.currency_code || ""}/${q.unit_code || "unit"} valid till ${q.sap_valid_to}`);
      } else if (q.xpa_price) {
        toast.error("SAP list price is in XPA (packet) — recreate it in EA with Base qty via List Prices.");
      } else if (q.not_smart_pr) {
        toast.error("SAP has a list price, but it was not created through Smart PR — raise it via List Prices first.");
      } else if (q.no_sap_price) {
        toast.error("Still no active SAP list price — check the supplier and release status in SAP BYD.");
      } else if (q.uom_mismatch) {
        toast.error("SAP has a price but the UoM doesn't match — see the line-level error.");
      }
    } catch (e) {
      updateItem(idx, { fetchingLp: false });
      toast.error(e?.response?.data?.detail || e?.message || "Fetch failed");
    }
  };

  const bopLpMissing = purcType === "BOP"
    ? items.filter((it) => it.icode && (it.noSapPrice || it.uomMismatch)).map((it) => it.icode)
    : [];

  const itemSearchTimer = useRef({});
  const searchItemFor = (idx, q) => {
    updateItem(idx, { _q: q });
    if (itemSearchTimer.current[idx]) clearTimeout(itemSearchTimer.current[idx]);
    if (q.length < 2) return;
    itemSearchTimer.current[idx] = setTimeout(async () => {
      try { const { data } = await createApi.searchItems(q); updateItem(idx, { _opts: data }); }
      catch (e) { console.warn("searchItems failed", e); }
    }, 250);
  };
  // Item search + pick. When the user picks an item that is ALREADY present
  // on another line of this PR, we DON'T create a duplicate row — instead
  // we add the current row's quantity onto the existing row and remove this
  // empty/duplicate row. This matches procurement convention (one icode per
  // PR, qty rolled up) and stops accidental dupes during back-and-forth edits.
  const pickItem = (idx, opt) => {
    setItems((arr) => {
      const dupIdx = arr.findIndex((it, i) => i !== idx && (it.icode || "").trim() === (opt.icode || "").trim() && (opt.icode || "").trim() !== "");
      if (dupIdx !== -1) {
        const current = arr[idx];
        const existing = arr[dupIdx];
        const addedQty = Math.max(Number(current.qty) || 0, 1);
        const newQty = (Number(existing.qty) || 0) + addedQty;
        toast.success(`"${opt.iname}" already on line ${dupIdx + 1} — quantity updated to ${newQty}.`);
        const merged = arr.map((it, i) =>
          i === dupIdx ? { ...it, qty: newQty } : it
        );
        // Remove the duplicate row that we just merged in. If it was the only
        // empty trailing row, restore one fresh blank row so the user can keep
        // adding items.
        const without = merged.filter((_, i) => i !== idx);
        return without.length === 0 ? [emptyItem()] : without;
      }
      // First time we've seen this icode on this PR — normal fill.
      // For BOP we don't pre-fill the picker's MSSQL rate — the strict
      // policy is SAP-list-price-only. The quote effect will fill the
      // rate from the SAP mirror on the next tick (or block the line
      // with an actionable error if no bounded SAP contract exists).
      const bopStrict = purcType === "BOP";
      return arr.map((it, i) => i === idx ? {
        ...it,
        icode: opt.icode, iname: opt.iname, unit: opt.unit || "",
        rate: bopStrict ? 0 : (opt.last_rate || 0),
        pricedBy: (!bopStrict && opt.last_rate) ? "picker_mssql" : undefined,
        mat_code: opt.mat_code != null ? opt.mat_code : "",
        sap_match: opt.sap_match,
        _opts: [], _q: opt.iname,
      } : it);
    });
  };

  const payload = () => ({
    pcode: supplier?.pcode || "", party: supplier?.party || "", purc_type: purcType, remark,
    auth_by: authBy || null, app_by: appBy || null,
    compcode, dept_id: deptId, material_group: matGroup,
    gst, end_cust: endCust, end_cust_name: endCustName,
    requestor_emp_no: (requestorEmpNo || "").trim(),
    requestor_name: (requestorName || "").trim(),
    currency, exch_rate: Number(exchRate) || 1, extra_per: 0,
    in_house: inHouse,
    items: items.filter((i) => i.icode).map((i) => ({
      icode: i.icode, iname: i.iname, qty: Number(i.qty), rate: Number(i.rate), unit: i.unit, remark: i.remark,
      extra_per: isKgm(i.unit) ? Number(i.extra_per) || 0 : 0, mat_code: i.mat_code ? Number(i.mat_code) : null,
    })),
  });

  const saveDraft = async () => {
    if (!supplier?.pcode) return toast.error("Pick a supplier");
    setSaving(true);
    try {
      const { data } = draftId
        ? await createApi.updateDraft(draftId, payload())
        : await createApi.saveDraft(payload());
      setDraftId(data.id);
      toast.success("Draft saved");
      loadDrafts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const submitNow = async () => {
    if (!supplier?.pcode) return toast.error("Pick a supplier");
    if (!(requestorEmpNo || "").trim()) return toast.error("Pick a Requestor from the suggestions");

    // Classification — all 4 fields are mandatory (cannot be blank)
    if (!deptId) return toast.error("Dept is required");
    if (!matGroup) return toast.error("Material Group is required");
    if (gst === "" || gst === null || gst === undefined) return toast.error("GST % is required (0 is allowed)");
    if (!endCust) return toast.error("End User is required");

    // Pricing — all 4 fields are mandatory. Numeric fields accept 0 but not blank.
    if (!currency) return toast.error("Currency is required");
    if (exchRate === "" || exchRate === null || exchRate === undefined || Number.isNaN(Number(exchRate))) {
      return toast.error("Exchange Rate is required (0 is allowed)");
    }
    const badExtra = items.find((i) => i.icode && Number(i.extra_per) > 0 && (!isKgm(i.unit) || Number(i.extra_per) > MAX_EXTRA_PER));
    if (badExtra) {
      return toast.error(`${badExtra.icode}: Extra % is allowed only for KGM items, max ${MAX_EXTRA_PER}%`);
    }
    if (!inHouse) return toast.error("In-House is required");

    const validItems = items.filter((i) => i.icode && Number(i.qty) > 0 && Number(i.rate) > 0);
    if (validItems.length === 0) return toast.error("Add at least one item (with qty & rate)");
    if (!matchedRule) return toast.error("No approval policy matches this PR — ask an Admin to configure rules");
    setSaving(true);
    try {
      if (isEditMode) {
        // Direct PUT — bypasses draft flow entirely. Backend enforces
        // creator-only + pre-requestor-review editable window.
        const { data } = await createApi.updatePR(editVocNo, payload());
        toast.success(data.auto_approved
          ? `PR #${data.voc_no} updated and AUTO-APPROVED (₹${fmtAmt(data.amount)})`
          : `PR #${data.voc_no} updated (₹${fmtAmt(data.amount)})`);
        setTimeout(() => nav("/my-prs"), 600);
        return;
      }
      // ALWAYS persist current form state before submit. If the user has an existing
      // (possibly stale / empty) draft loaded, we must push current items into it,
      // otherwise the backend submits whatever items the old draft had.
      const { data: persisted } = draftId
        ? await createApi.updateDraft(draftId, payload())
        : await createApi.saveDraft(payload());
      const id = persisted.id;
      if (!draftId) setDraftId(id);
      const { data } = await createApi.submitDraft(id);
      if (data.auto_approved) {
        toast.success(`PR #${data.voc_no} submitted and AUTO-APPROVED (₹${fmtAmt(data.amount)}) — requestor, L1 and L2 have been informed`, { duration: 8000 });
      } else {
        toast.success(`PR #${data.voc_no} submitted (₹${fmtAmt(data.amount)})`);
      }
      // If we arrived from the MRP Proposals inbox, mark the source proposal
      // as converted so it grey-outs there. Non-fatal on failure — the PR
      // itself already exists; a manual retry-from-admin is available.
      if (mrpProposalId && data.voc_no) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/mrp-proposals/${mrpProposalId}/mark-converted`,
            { voc_no: data.voc_no,
              supplier_code: supplier?.pcode,
              supplier_name: supplier?.party,
              rate: (items.find(i => i.icode && i.rate > 0) || {}).rate,
              note: "Converted via CreatePR" },
            { headers: { Authorization: `Bearer ${localStorage.getItem("pr_token")}` } },
          );
        } catch (err) {
          console.warn("mark-converted failed (non-fatal):", err);
        }
        setMrpProposalId(null);
      }
      setSupplier(null); setItems([emptyItem()]); setRemark(""); setDraftId(null);
      setAuthBy(""); setAppBy("");
      setCompcode("P1"); setDeptId(""); setMatGroup(""); setGst("18");
      setEndCust(""); setEndCustName(""); setCurrency("INR"); setExchRate(1);
      setInHouse("No");
      setRequestorName(me?.full_name || ""); setRequestorEmpNo(me?.emp_no || ""); setRequestorQ(""); setRequestorOpts([]);
      loadDrafts();
      setTimeout(() => nav("/my-prs"), 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setSaving(false); }
  };

  const loadDraft = (d) => {
    setDraftId(d.id);
    setSupplier({ pcode: d.pcode, party: d.party || d.pcode });
    setPurcType(d.purc_type);
    setRemark(d.remark || "");
    setAuthBy(d.auth_by || "");
    setAppBy(d.app_by || "");
    setCompcode(d.compcode || "P1");
    setDeptId(d.dept_id || "");
    setMatGroup(d.material_group || "");
    setGst(d.gst || "18");
    setEndCust(d.end_cust || "");
    setEndCustName(d.end_cust_name || "");
    setRequestorName(d.requestor_name || me?.full_name || "");
    setRequestorEmpNo(d.requestor_emp_no || me?.emp_no || "");
    setCurrency(d.currency || "INR");
    setExchRate(d.exch_rate ?? 1);
    setInHouse(d.in_house || "No");
    setItems((d.items || []).map((it) => ({ ...emptyItem(), ...it, extra_per: clampExtra(it.unit, it.extra_per) })).concat(emptyItem()));
  };

  const total = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
  const totalINR = total * (Number(exchRate) || 1);
  const action = preview?.ai_action || "APPROVE";
  const risk = preview?.risk_score || 0;

  // Evaluate active rules (priority desc) for current totalINR + purcType — first match wins.
  const matchedRule = policyRules.find((r) => {
    if (r.purc_types?.length && !r.purc_types.includes(purcType)) return false;
    if (r.min_amount != null && totalINR < Number(r.min_amount)) return false;
    if (r.max_amount != null && totalINR >= Number(r.max_amount)) return false;
    return true;
  }) || null;
  const requiredL1 = matchedRule?.l1_emp_no || "";
  // L2 may have multiple choices; default to the first, but let creator pick.
  const l2Options = (matchedRule?.l2_choices) || [];
  const twoLevel = l2Options.length > 0 && (l2Options[0]?.emp_no !== requiredL1);
  const requiredL2 = twoLevel ? (l2Options[0]?.emp_no || "") : "";

  // Auto-sync L1 / L2 selections whenever rule match changes.
  useEffect(() => {
    if (!policyRules.length) return;
    if (authBy !== requiredL1) setAuthBy(requiredL1);
    // Reset appBy when matched rule changes — pick default (first allowed), keep user's choice if still valid.
    const allowed = l2Options.map((o) => o.emp_no);
    if (!twoLevel) {
      if (appBy !== "") setAppBy("");
    } else if (!allowed.includes(appBy)) {
      setAppBy(requiredL2);
    }
  }, [policyRules, requiredL1, requiredL2, twoLevel, authBy, appBy, l2Options]);


  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-cyan-700 text-white sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto h-full px-3 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => nav("/my-prs")} className="text-white hover:bg-white/10" data-testid="back-link">
              <ArrowLeft size={16} className="mr-1" /> My PRs
            </Button>
            <div className="h-6 w-px bg-white/20 hidden sm:block" />
            <div className="flex items-center gap-2"><FileText size={20} /><div className="text-lg sm:text-xl font-bold tracking-tight" data-testid="pr-page-title">{isEditMode ? `Edit PR #${editVocNo}` : "Create PR"}</div></div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ItemMasterSyncButton />
            <WorkspaceSwitcher role={me?.role} />
            <div className="text-xs text-cyan-100 hidden sm:block">{me?.full_name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-3 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Header card — JDE-style banded */}
          <div className="jde-form rounded-sm overflow-hidden">
            <div className="jde-band">PR Header</div>
            <div className="jde-section grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Supplier *</Label>
                <Input
                  data-testid="supplier-search"
                  placeholder="Search supplier name or code..."
                  value={supplier ? `${supplier.pcode} · ${supplier.party}` : suppQuery}
                  onChange={(e) => { setSupplier(null); setSuppQuery(e.target.value); }}
                  className="mt-1"
                />
                {!supplier && suppOptions.length > 0 && (
                  <div className="border border-slate-200 rounded mt-1 bg-white shadow-sm max-h-44 overflow-y-auto">
                    {suppOptions.map((s) => (
                      <button
                        key={s.pcode}
                        data-testid={`pick-supplier-${s.pcode}`}
                        onClick={() => { setSupplier(s); setSuppQuery(""); setSuppOptions([]); }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-cyan-50"
                      >
                        <span className="mono text-xs text-slate-500">{s.pcode}</span> · {s.party}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:col-span-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Plant *</Label>
                  <Select value={compcode} onValueChange={setCompcode}>
                    <SelectTrigger data-testid="plant" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{masters.plants.map((p) => <SelectItem key={p.code} value={p.code}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Purchase Type *</Label>
                  <Select value={purcType} onValueChange={setPurcType}>
                    <SelectTrigger data-testid="purc-type" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{(masters.purc_types.length ? masters.purc_types : ["BOP","Consume","Service","Capital","Jobwork"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="jde-band">Classification</div>
            <div className="jde-section grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Dept <span className="text-red-500">*</span></Label>
                <Combobox
                  testid="dept-id"
                  options={masters.depts.map((d) => ({ value: d.id, label: d.name, sub: `Dept #${d.id}` }))}
                  value={deptId}
                  onChange={setDeptId}
                  placeholder="—"
                  searchPlaceholder="Search dept…"
                  className="mt-1"
                />
              </div>              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Material Group <span className="text-red-500">*</span></Label>
                <Combobox
                  testid="mat-group"
                  options={masters.material_types
                    .filter((m) => (m.name || "").trim())
                    .map((m) => ({ value: String(m.name).slice(0, 10), label: `${m.code} · ${m.name}`, sub: m.name }))}
                  value={matGroup}
                  onChange={setMatGroup}
                  placeholder="—"
                  searchPlaceholder="Search type…"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">GST % <span className="text-red-500">*</span></Label>
                <Select value={gst} onValueChange={setGst}>
                  <SelectTrigger data-testid="gst" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{(masters.gst_rates.length ? masters.gst_rates : ["0","5","12","18","28"]).map((g) => <SelectItem key={g} value={g}>{g}%</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">End User <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    data-testid="end-cust"
                    placeholder="Search code or name…"
                    value={endCust ? `${endCust} · ${endCustName}` : endCustQ}
                    onChange={(e) => { setEndCust(""); setEndCustName(""); setEndCustQ(e.target.value); }}
                    onFocus={(e) => { if (endCust) { setEndCust(""); setEndCustName(""); setEndCustQ(""); } }}
                    className="mt-1"
                  />
                  {!endCust && endCustOpts.length > 0 && (
                    <div className="absolute z-30 left-0 right-0 border border-slate-200 rounded mt-1 bg-white shadow-lg max-h-44 overflow-y-auto">
                      {endCustOpts.map((s) => (
                        <button
                          key={s.pcode}
                          type="button"
                          data-testid={`pick-endcust-${s.pcode}`}
                          onClick={() => { setEndCust(s.pcode); setEndCustName(s.party); setEndCustQ(""); setEndCustOpts([]); }}
                          className="block w-full text-left px-3 py-2 text-sm hover:bg-cyan-50"
                        >
                          <span className="mono text-xs text-slate-500">{s.pcode}</span> · {s.party}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Requestor *</Label>
                <div className="relative">
                  <Input
                    data-testid="requestor-name"
                    placeholder="Type a name (or keep yourself)…"
                    value={requestorQ || (requestorEmpNo ? `${requestorName} · ${requestorEmpNo}` : "")}
                    onChange={(e) => { setRequestorEmpNo(""); setRequestorName(""); setRequestorQ(e.target.value); }}
                    onFocus={() => { if (requestorEmpNo && !requestorQ) { setRequestorQ(requestorName); setRequestorEmpNo(""); setRequestorName(""); } }}
                    className="mt-1"
                  />
                  {!requestorEmpNo && requestorOpts.length > 0 && (
                    <div className="absolute z-30 left-0 right-0 border border-slate-200 rounded mt-1 bg-white shadow-lg max-h-44 overflow-y-auto">
                      {requestorOpts.map((u) => (
                        <button
                          key={u.emp_no}
                          type="button"
                          data-testid={`pick-requestor-${u.emp_no}`}
                          onClick={() => { setRequestorEmpNo(u.emp_no); setRequestorName(u.full_name || u.username || ""); setRequestorQ(""); setRequestorOpts([]); }}
                          className="block w-full text-left px-3 py-2 text-sm hover:bg-cyan-50"
                        >
                          <span className="mono text-xs text-slate-500">{u.emp_no}</span> · {u.full_name}
                          {u.username ? <span className="text-xs text-slate-400 ml-2">({u.username})</span> : null}
                        </button>
                      ))}
                    </div>
                  )}
                  {!requestorEmpNo && requestorQ.length >= 2 && requestorOpts.length === 0 && (
                    <div className="text-xs text-amber-700 mt-1" data-testid="requestor-no-match">No active employee matches — must be picked from suggestions.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="jde-band">Pricing</div>
            <div className="jde-section grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Currency <span className="text-red-500">*</span></Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="currency" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{(masters.currencies.length ? masters.currencies : ["INR","USD","EUR","GBP"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Exch. Rate <span className="text-red-500">*</span></Label>
                <Input data-testid="exch-rate" type="number" step="0.0001" value={exchRate} onChange={(e) => setExchRate(e.target.value)} disabled={currency === "INR"} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">In-House <span className="text-red-500">*</span></Label>
                <Select value={inHouse} onValueChange={setInHouse}>
                  <SelectTrigger data-testid="in-house" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="jde-band">Routing &amp; Notes</div>
            <div className="jde-section grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Remark</Label>
                <Input value={remark} onChange={(e) => setRemark(e.target.value)} className="mt-1" data-testid="remark" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Authoriser (L1) *</Label>
                <div
                  data-testid="auth-by-locked"
                  className="mt-1 px-3 py-2 rounded-sm border border-slate-300 bg-slate-100 text-[12.5px] text-slate-800 flex items-center justify-between gap-2"
                  title="Fixed by policy"
                >
                  <span className="truncate">{matchedRule?.l1_name || (policyRules.length ? "—" : "Loading…")}</span>
                  <span className="text-[10px] text-slate-500 mono flex-shrink-0">{matchedRule?.l1_emp_no || ""}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Approver (L2){twoLevel ? " *" : ""}</Label>
                {twoLevel ? (
                  l2Options.length > 1 ? (
                    <Select value={appBy || requiredL2} onValueChange={setAppBy}>
                      <SelectTrigger data-testid="app-by-select" className="mt-1">
                        <SelectValue placeholder="Select approver…" />
                      </SelectTrigger>
                      <SelectContent>
                        {l2Options.map((o) => (
                          <SelectItem key={o.emp_no} value={o.emp_no}>
                            {o.full_name} <span className="text-slate-400 text-xs">· {o.emp_no}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div
                      data-testid="app-by-locked"
                      className="mt-1 px-3 py-2 rounded-sm border border-slate-300 bg-slate-100 text-[12.5px] text-slate-800 flex items-center justify-between gap-2"
                      title="Fixed by policy"
                    >
                      <span className="truncate">{l2Options[0]?.full_name || "—"}</span>
                      <span className="text-[10px] text-slate-500 mono flex-shrink-0">{l2Options[0]?.emp_no || ""}</span>
                    </div>
                  )
                ) : (
                  <div
                    data-testid="app-by-skipped"
                    className="mt-1 px-3 py-2 rounded-sm border border-dashed border-slate-300 bg-white text-[12px] text-slate-500 italic"
                    title="Single-level approval applies for this PR"
                  >
                    Not required &middot; single-level
                  </div>
                )}
              </div>
              {matchedRule && (
                <div className="sm:col-span-4 text-[11px] text-slate-600 bg-cyan-50 border border-cyan-200 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="routing-policy-hint">
                  <Sparkles size={12} className="mt-0.5 text-cyan-700 flex-shrink-0" />
                  <span>
                    Matched policy: <b>{matchedRule.name}</b> &middot; routes to <b>{matchedRule.l1_name}</b>
                    {twoLevel && <> &rarr; <b>{matchedRule.l2_name}</b></>}
                    {!twoLevel && <> &middot; <span className="italic">single-level</span></>}
                  </span>
                </div>
              )}
              {!matchedRule && policyRules.length > 0 && (
                <div className="sm:col-span-4 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2" data-testid="routing-policy-no-match">
                  No approval policy matches this PR (amount / type). Ask an Admin to configure rules in Admin &rarr; Approval Policy.
                </div>
              )}
            </div>
          </div>
          <div className="jde-form rounded-sm overflow-hidden">
            <div className="jde-band flex items-center justify-between">
              <span>Line Items</span>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item" className="h-6 px-2 bg-white text-cyan-800 hover:bg-cyan-50 border-cyan-300 text-[11px]">
                <Plus size={12} className="mr-1" />Add
              </Button>
            </div>
            <div className="jde-section space-y-2">
              {bopSapMissing.length > 0 && (
                <div className="rounded-sm border border-rose-300 bg-rose-50 px-3 py-2 text-[11px] text-rose-800 flex items-start gap-2"
                     data-testid="bop-sap-missing-banner">
                  <AlertTriangle size={14} className="text-rose-600 mt-[1px] flex-shrink-0" />
                  <div className="leading-snug">
                    <span className="font-semibold uppercase tracking-wide">
                      {bopSapMissing.length} item{bopSapMissing.length === 1 ? "" : "s"} not in SAP master —
                    </span>{" "}
                    this Purchase Requisition will be blocked at submit until the following item code
                    {bopSapMissing.length === 1 ? " is" : "s are"} registered in SAP (or replaced with
                    {" "}an equivalent SAP-registered item):
                    <span className="mono ml-1 font-semibold">
                      {bopSapMissing.slice(0, 5).join(", ")}
                      {bopSapMissing.length > 5 ? ` (+${bopSapMissing.length - 5} more)` : ""}
                    </span>
                  </div>
                </div>
              )}
              {bopBomMissing.length > 0 && (
                <div className="rounded-sm border border-rose-300 bg-rose-50 px-3 py-2 text-[11px] text-rose-800 flex items-start gap-2"
                     data-testid="bop-bom-missing-banner">
                  <AlertTriangle size={14} className="text-rose-600 mt-[1px] flex-shrink-0" />
                  <div className="leading-snug">
                    <span className="font-semibold uppercase tracking-wide">
                      {bopBomMissing.length} item{bopBomMissing.length === 1 ? "" : "s"} not in any Released BOM —
                    </span>{" "}
                    only pallets (<span className="mono">PALL-*</span>) are exempt. Add the following
                    {bopBomMissing.length === 1 ? " item" : " items"} to the consuming BOM in SAP
                    before submitting:
                    <span className="mono ml-1 font-semibold">
                      {bopBomMissing.slice(0, 5).join(", ")}
                      {bopBomMissing.length > 5 ? ` (+${bopBomMissing.length - 5} more)` : ""}
                    </span>
                  </div>
                </div>
              )}
              {bopPurchInactive.length > 0 && (
                <div className="rounded-sm border border-rose-300 bg-rose-50 px-3 py-2 text-[11px] text-rose-800 flex items-start gap-2"
                     data-testid="bop-purch-inactive-banner">
                  <AlertTriangle size={14} className="text-rose-600 mt-[1px] flex-shrink-0" />
                  <div className="leading-snug">
                    <span className="font-semibold uppercase tracking-wide">
                      {bopPurchInactive.length} item{bopPurchInactive.length === 1 ? "" : "s"} with Purchasing view not Active —
                    </span>{" "}
                    a PO can't be released against a material whose SAP Purchasing view is Blocked, In Preparation, or missing.
                    Have the Purchasing view released to <b>Active</b> in SAP BYD for:
                    <span className="mono ml-1 font-semibold">
                      {bopPurchInactive.slice(0, 5).join(", ")}
                      {bopPurchInactive.length > 5 ? ` (+${bopPurchInactive.length - 5} more)` : ""}
                    </span>
                  </div>
                </div>
              )}
              {bopLpMissing.length > 0 && (
                <div className="rounded-sm border border-rose-300 bg-rose-50 px-3 py-2 text-[11px] text-rose-800 flex items-start gap-2"
                     data-testid="bop-lp-missing-banner">
                  <AlertTriangle size={14} className="text-rose-600 mt-[1px] flex-shrink-0" />
                  <div className="leading-snug">
                    <span className="font-semibold uppercase tracking-wide">
                      {bopLpMissing.length} line{bopLpMissing.length === 1 ? "" : "s"} without an active SAP list price —
                    </span>{" "}
                    submit is disabled until every BOP line resolves to a bounded SAP list
                    price for the selected supplier. Upload / release the price in SAP BYD
                    (Products → Materials → {"<item>"} → Price Specifications), then click
                    <span className="font-semibold"> Fetch Latest LP </span>on the row.
                    Missing: <span className="mono ml-1 font-semibold">
                      {bopLpMissing.slice(0, 5).join(", ")}
                      {bopLpMissing.length > 5 ? ` (+${bopLpMissing.length - 5} more)` : ""}
                    </span>
                  </div>
                </div>
              )}
              {items.map((it, idx) => {
                const pv = preview?.items?.find((x) => x.icode === it.icode);
                return (
                  <div key={it._key || idx} className="border border-slate-300 bg-slate-50 rounded-sm p-3 space-y-2" data-testid={`line-${idx}`}>
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 relative">
                        <div className="relative">
                          <Search size={12} className="absolute left-2 top-2.5 text-slate-400" />
                          <Input
                            placeholder="Search item name or code..."
                            value={it._q !== "" ? it._q : (it.iname || "")}
                            onChange={(e) => searchItemFor(idx, e.target.value)}
                            className="pl-7"
                            data-testid={`item-search-${idx}`}
                          />
                        </div>
                        {it._opts?.length > 0 && (
                          <div className="absolute z-10 w-full border border-slate-200 rounded mt-1 bg-white shadow-md max-h-44 overflow-y-auto">
                            {it._opts.map((o) => o.not_in_erp ? (
                              <div key={`noerp-${o.icode}`} className="px-3 py-2 text-xs bg-rose-50 text-rose-800 border-t border-rose-200 cursor-not-allowed"
                                   data-testid={`item-not-in-erp-${idx}`}>
                                <span className="mono font-semibold">{o.icode}</span>{o.iname ? ` · ${o.iname}` : ""}
                                <div className="font-semibold mt-0.5">Create Item in ERP Portal</div>
                                <div className="text-[11px] text-rose-700">This item exists in SAP but not in the ERP item master, so it can't be added to a PR yet.</div>
                              </div>
                            ) : (
                              <button key={o.icode} onClick={() => pickItem(idx, o)}
                                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-cyan-50">
                                <span className="mono text-slate-500">{o.icode}</span> · {o.iname}
                                {o.last_rate && <span className="text-slate-400 ml-2">last ₹{fmtAmt(o.last_rate)}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                        {it.icode && (
                          <div className="text-[10px] mt-1 flex items-center gap-1.5 flex-wrap">
                            <span className="text-slate-500 mono" data-testid={`row-icode-${idx}`}>{it.icode}</span>
                            {purcType === "BOP" && it.sap_match === true && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-700 font-semibold uppercase tracking-wide"
                                    data-testid={`row-sap-ok-${idx}`}
                                    title="This item resolves in the SAP material master — the PR will submit fine.">
                                ✓ In SAP
                              </span>
                            )}
                            {purcType === "BOP" && it.sap_match === false && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-sm border border-rose-300 bg-rose-50 text-rose-700 font-semibold uppercase tracking-wide"
                                    data-testid={`row-sap-missing-${idx}`}
                                    title="This item is NOT registered in the SAP material master. The submit will be blocked until master data adds it (or you pick an equivalent SAP-registered item).">
                                ⚠ Not in SAP
                              </span>
                            )}
                            {purcType === "BOP" && it.in_bom === false && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-sm border border-rose-300 bg-rose-50 text-rose-700 font-semibold uppercase tracking-wide"
                                    data-testid={`row-bom-missing-${idx}`}
                                    title="This item is not consumed in any Released BOM in SAP. Only pallets (PALL-*) are exempt — everything else must be part of a BOM before a PR can be raised.">
                                ⚠ Not in BOM
                              </span>
                            )}
                            {purcType === "BOP" && it.purch_active === false && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-sm border border-rose-300 bg-rose-50 text-rose-700 font-semibold uppercase tracking-wide"
                                    data-testid={`row-purch-inactive-${idx}`}
                                    title={`Purchasing view in SAP is "${it.purch_status_label || "not Active"}". Have it released to status Active before submitting the PR.`}>
                                ⚠ {it.purch_status_label || "Purchasing inactive"}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-rose-600" data-testid={`remove-${idx}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-cyan-700 font-semibold">Qty</Label>
                        <Input type="number" value={it.qty} onChange={(e) => updateItem(idx, { qty: e.target.value })} data-testid={`qty-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-cyan-700 font-semibold">Unit</Label>
                        <Input value={it.unit} onChange={(e) => updateItem(idx, { unit: e.target.value })} data-testid={`unit-${idx}`} placeholder="Pcs" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-cyan-700 font-semibold">Rate {currency !== "INR" ? currency : "₹"}</Label>
                        <Input type="number" value={it.rate}
                               readOnly={purcType === "BOP"}
                               tabIndex={purcType === "BOP" ? -1 : 0}
                               onChange={(e) => updateItem(idx, { rate: e.target.value, pricedBy: undefined })}
                               data-testid={`rate-${idx}`}
                               className={purcType === "BOP" ? "bg-slate-100 cursor-not-allowed" : ""}
                               title={purcType === "BOP" ? "BOP rates are auto-set from SAP list price and cannot be edited manually." : ""} />
                        {purcType === "BOP" && it.icode && (
                          <div className="flex items-center gap-1 mt-1">
                            <button type="button"
                                    onClick={() => fetchLatestLp(idx)}
                                    onTouchEnd={(e) => { e.preventDefault(); fetchLatestLp(idx); }}
                                    disabled={!!it.fetchingLp || !supplier?.pcode}
                                    className="text-[11px] px-2.5 min-h-[36px] w-full sm:w-auto rounded-sm border border-cyan-300 bg-cyan-50 text-cyan-800 hover:bg-cyan-100 active:bg-cyan-200 font-semibold uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 touch-manipulation select-none"
                                    style={{ WebkitTapHighlightColor: "rgba(6,182,212,0.35)" }}
                                    data-testid={`fetch-lp-${idx}`}
                                    title="Live-fetch the latest SAP list price for this supplier×material and upsert it into the local mirror. Use after uploading / releasing a price in SAP BYD.">
                              {it.fetchingLp ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                              {it.fetchingLp ? "Fetching…" : "Fetch Latest LP"}
                            </button>
                          </div>
                        )}
                        {purcType === "BOP" && it.rateSource === "sap_list_price" && !it.uomMismatch && (
                          <div className="text-[10px] mt-0.5 flex items-center gap-1 flex-wrap text-emerald-700"
                               data-testid={`rate-source-sap-${idx}`}
                               title={`Bounded SAP contract. Valid ${it.sapValidFrom || "?"} → ${it.sapValidTo === "9999-12-31" ? "open" : (it.sapValidTo || "?")}. Currency ${it.rateCurrency || "INR"}, UoM ${it.rateUnitCode || ""}.`}>
                            <span className="font-semibold uppercase tracking-wide">SAP</span>
                            <span className="text-slate-500 font-mono">
                              · {it.sapValidFrom ? fmtDate(it.sapValidFrom) : "?"}
                              <span className="text-slate-400"> → </span>
                              {it.sapValidTo === "9999-12-31"
                                 ? <span className="text-emerald-700 font-bold">open</span>
                                 : (it.sapValidTo ? fmtDate(it.sapValidTo) : "?")}
                            </span>
                          </div>
                        )}
                        {purcType === "BOP" && it.rateSource === "sap_list_price" && !it.uomMismatch && Number(it.sapBaseQty || 1) !== 1 && (
                          <div className="text-[10px] mt-0.5 text-cyan-800 font-mono"
                               data-testid={`rate-sap-basis-${idx}`}>
                            SAP {it.sapAmount} {it.rateCurrency || ""} / {it.sapBaseQty} {it.rateUnitCode || ""} = {it.rate}/{it.rateUnitCode || "unit"}
                          </div>
                        )}
                        {purcType === "BOP" && it.noSapPrice && !it.uomMismatch && (
                          <div className="text-[10px] mt-0.5 text-rose-700 leading-snug"
                               data-testid={`rate-no-sap-${idx}`}>
                            <span className="font-semibold uppercase tracking-wide">{it.lpBlockLabel || (it.notSmartPr ? "Not created via Smart PR —" : "No SAP list price —")}</span>{" "}
                            {it.noSapPriceMsg}
                          </div>
                        )}
                        {purcType === "BOP" && it.uomMismatch && (
                          <div className="text-[10px] mt-0.5 text-rose-700 leading-snug"
                               data-testid={`rate-uom-mismatch-${idx}`}>
                            <span className="font-semibold uppercase tracking-wide">UoM mismatch —</span>{" "}
                            {it.uomMismatchMsg}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-cyan-700 font-semibold">Extra %</Label>
                        <Input type="number" step="1" min="0" max={MAX_EXTRA_PER}
                          value={isKgm(it.unit) ? (it.extra_per ?? 0) : 0}
                          disabled={!isKgm(it.unit)}
                          title={isKgm(it.unit) ? `Max ${MAX_EXTRA_PER}%` : "Extra % allowed only for KGM items"}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateItem(idx, { extra_per: v === "" ? "" : Math.min(MAX_EXTRA_PER, Math.max(0, Number(v))) });
                          }}
                          data-testid={`extra-${idx}`} />
                        <div className="text-[10px] mt-0.5 text-slate-500" data-testid={`extra-hint-${idx}`}>
                          {isKgm(it.unit) ? `KGM only · max ${MAX_EXTRA_PER}%` : "Only for KGM items"}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-cyan-700 font-semibold">Mat Code</Label>
                        <Combobox
                          testid={`mat-${idx}`}
                          options={masters.material_types.map((m) => ({ value: m.code, label: m.name, sub: `#${m.code}` }))}
                          value={it.mat_code != null ? String(it.mat_code) : ""}
                          onChange={(v) => updateItem(idx, { mat_code: v ? Number(v) : "" })}
                          placeholder="—"
                          searchPlaceholder="Search material…"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-cyan-700 font-semibold">Amt {currency !== "INR" ? `(${currency})` : "₹"}</Label>
                        <div className="num text-sm font-semibold pt-2">{fmtAmt((Number(it.qty) || 0) * (Number(it.rate) || 0))}</div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-cyan-700 font-semibold">Item Remark</Label>
                      <Input value={it.remark || ""} onChange={(e) => updateItem(idx, { remark: e.target.value })} data-testid={`item-remark-${idx}`} className="h-8" />
                    </div>
                    {pv && (
                      <div className="flex flex-wrap items-center gap-2 pt-1" data-testid={`ai-preview-${idx}`}>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${flagClass(pv.price_flag.flag)}`}>
                          Price · {priceLabel(pv.price_flag)}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${flagClass(pv.vendor_flag.flag)}`}>
                          Vendor · {vendorLabel(pv.vendor_flag)}
                        </span>
                        {pv.vendor_flag.lowest_vendor && pv.vendor_flag.flag !== "green" && (
                          <span className="text-xs text-slate-700">
                            {pv.vendor_flag.same_vendor ? (
                              <>Cheaper elsewhere: </>
                            ) : (
                              <>→ </>
                            )}
                            <b>{pv.vendor_flag.lowest_vendor}</b> @ ₹{fmtAmt(pv.vendor_flag.lowest_rate)}
                            {pv.vendor_flag.lowest_vdate && (
                              <span className="text-slate-500"> &middot; {fmtDate(pv.vendor_flag.lowest_vdate)}</span>
                            )}
                          </span>
                        )}
                        {pv.last_rate ? (
                          <span className="text-xs text-slate-500">
                            was ₹{fmtAmt(pv.last_rate)}
                            {pv.last_vdate && (
                              <span className="text-slate-400"> on {fmtDate(pv.last_vdate)}</span>
                            )}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Grand total */}
            {items.some((i) => i.icode) && (
              <div className="mt-3 pt-3 border-t-2 border-slate-200 flex items-center justify-between flex-wrap gap-2" data-testid="pr-grand-total">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">PR Total</div>
                <div className="flex items-baseline gap-3">
                  <span className="text-xs text-slate-500">{items.filter((i) => i.icode).length} item{items.filter((i) => i.icode).length === 1 ? "" : "s"}</span>
                  <span className="text-lg font-bold text-slate-700 num">{currency} {fmtAmt(total)}</span>
                  {currency !== "INR" && (
                    <span className="text-2xl font-extrabold text-cyan-800 num">≈ ₹{fmtAmt(totalINR)}</span>
                  )}
                  {currency === "INR" && (
                    <span className="text-2xl font-extrabold text-cyan-800 num">₹{fmtAmt(total)}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right pane */}
        <aside className="space-y-4">
          <div className={`rounded-sm p-4 border ${action === "HOLD" ? "bg-rose-50 border-rose-200" : action === "REVIEW" ? "bg-yellow-50 border-yellow-200" : "bg-emerald-50 border-emerald-200"}`}>
            <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">AI Pre-check</div>
            <div className="flex items-center justify-between mt-2">
              <div>
                <div className="text-3xl font-extrabold num">{risk}</div>
                <div className="text-xs text-slate-600">Risk score</div>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${actionClass(action)}`} data-testid="risk-action">
                {previewBusy && <Loader2 className="animate-spin mr-1" size={12} />}
                {action}
              </span>
            </div>
            {action !== "APPROVE" && (
              <div className="mt-2 text-[11px] text-slate-700 flex items-start gap-1">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                <span>This PR will be flagged at approval. Review highlighted items before submitting.</span>
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Summary</div>
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm text-slate-600">Items</span>
              <span className="text-lg font-bold text-slate-900 num">{items.filter((i) => i.icode).length}</span>
            </div>
            {currency !== "INR" && (
              <div className="flex items-baseline justify-between mt-1 text-sm">
                <span className="text-slate-500">{currency} total</span>
                <span className="num font-semibold">{currency} {fmtAmt(total)}</span>
              </div>
            )}
            <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-slate-100">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">PR Total (INR)</span>
              <span className="text-2xl font-extrabold text-cyan-800 num" data-testid="summary-total">₹{fmtAmt(totalINR)}</span>
            </div>
          </div>

          {matchedRule && (
            <div className="bg-white border border-slate-200 rounded-sm p-3 text-[11px] space-y-1" data-testid="routing-summary">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Routing</div>
              <div className="flex justify-between"><span className="text-slate-500">L1 Authoriser</span><span className="font-medium">{matchedRule.l1_name || "—"}</span></div>
              <div className="flex justify-between">
                <span className="text-slate-500">L2 Approver</span>
                <span className="font-medium">{twoLevel ? (matchedRule.l2_name || "—") : <span className="text-slate-400 italic">single-level</span>}</span>
              </div>
              <div className="flex justify-between pt-1 mt-1 border-t border-slate-100"><span className="text-slate-500">Policy</span><span className="font-medium text-cyan-700 truncate ml-2" title={matchedRule.name}>{matchedRule.name}</span></div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-2 gap-2">
            {isEditMode ? (
              <>
                <Button variant="outline" onClick={() => nav("/my-prs")} disabled={saving} data-testid="cancel-edit">
                  Cancel
                </Button>
                <Button onClick={submitNow} disabled={saving || !supplier || bopLpMissing.length > 0 || bopSapMissing.length > 0 || bopBomMissing.length > 0 || bopPurchInactive.length > 0} className="bg-cyan-700 hover:bg-cyan-600" data-testid="save-pr-edit">
                  <Save size={14} className="mr-1" />Save changes
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="save-draft">
                  <Save size={14} className="mr-1" />Save Draft
                </Button>
                <Button onClick={submitNow} disabled={saving || !supplier || bopLpMissing.length > 0 || bopSapMissing.length > 0 || bopBomMissing.length > 0 || bopPurchInactive.length > 0} className="bg-cyan-700 hover:bg-cyan-600" data-testid="submit-pr">
                  <Send size={14} className="mr-1" />Submit
                </Button>
              </>
            )}
          </div>

          {!isEditMode && drafts.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1">
                <Sparkles size={12} /> My Drafts
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {drafts.map((d) => (
                  <button key={d.id} onClick={() => loadDraft(d)} data-testid={`draft-${d.id}`}
                    className={`block w-full text-left p-2 rounded text-xs hover:bg-cyan-50 border ${draftId === d.id ? "border-purple-300 bg-cyan-50" : "border-transparent"}`}>
                    <div className="font-semibold">{d.pcode} · {d.purc_type}</div>
                    <div className="text-slate-500">{(d.items || []).length} items</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}
